import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { workItemApi } from '../services/api';
import { useAppStore } from '../store/useAppStore';

/**
 * Canonical deep-link entry point for a single work item, e.g.
 *   /item/task:a1b2c3d4      (internal _id)
 *   /item/TS-12              (human-readable displayId)
 * Other apps / emails can construct this path from either identifier. We
 * resolve the item to land the user on its project board for context, then
 * hand off to the `?item=<_id>` query param which opens the detail drawer.
 * Falls back to the global board if the item can't be resolved.
 */
export default function ItemRedirectPage() {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const setDetailItemId = useAppStore((s) => s.setDetailItemId);

  useEffect(() => {
    if (!itemId) return;
    let cancelled = false;

    const openOn = (id: string, path: string) => {
      if (cancelled) return;
      setDetailItemId(id);
      navigate(`${path}?item=${encodeURIComponent(id)}`, { replace: true });
    };

    workItemApi
      .get(itemId)
      .then((item) => {
        // Normalize to the canonical _id even when linked via displayId.
        const resolvedId = item?._id || itemId;
        openOn(resolvedId, item?.projectId ? `/project/${item.projectId}/board` : '/board');
      })
      .catch(() => openOn(itemId, '/board'));

    return () => {
      cancelled = true;
    };
  }, [itemId, navigate, setDetailItemId]);

  return <div className="p-8 text-sm text-gray-500 dark:text-gray-400">Opening item…</div>;
}
